import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/use-auth';
import { getErrorMessage } from '@/lib/errors';
import { destinationAfterLogin } from '@/lib/redirect';
import { googleRedirectUri, takeStoredGoogleState } from '@/modules/auth/google-oauth';

/**
 * Where Google sends the browser back to.
 *
 * ## The state check happens before anything else
 *
 * Google returns the `state` this app minted at the start. If it does not match what this browser
 * stored, the flow was not started here — which is exactly what login CSRF looks like: an attacker
 * completes their own Google sign-in, keeps the callback URL, and gets somebody else to open it,
 * quietly signing that person into the attacker's account. Refusing an unmatched state is the whole
 * defence, and it costs one comparison.
 *
 * ## Why the exchange runs once
 *
 * The authorization code is single-use. React's development mode mounts effects twice, and a second
 * exchange with a spent code fails — producing an error page after a sign-in that actually worked.
 * The ref makes the attempt idempotent regardless of how many times the effect runs.
 */
export function GoogleCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signInWithGoogle, completeTwoFactor } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) {
      return;
    }
    attempted.current = true;

    const returnedState = params.get('state');
    const expectedState = takeStoredGoogleState();
    const authCode = params.get('code');
    const googleError = params.get('error');

    if (googleError !== null) {
      // Usually `access_denied` — somebody pressed cancel. Not an error worth alarming them about.
      setError(
        googleError === 'access_denied'
          ? 'Sign-in was cancelled.'
          : 'Google could not complete that sign-in.',
      );

      return;
    }

    if (authCode === null || returnedState === null || expectedState === null) {
      setError('That sign-in link is incomplete. Please start again from the sign-in page.');

      return;
    }

    if (returnedState !== expectedState) {
      setError('That sign-in could not be verified as starting from this browser. Please try again.');

      return;
    }

    void (async () => {
      try {
        const result = await signInWithGoogle(authCode, googleRedirectUri());

        if (result.two_factor_required) {
          setChallengeToken(result.challenge_token);
        } else {
          navigate(destinationAfterLogin(null, result.user), { replace: true });
        }
      } catch (err) {
        setError(getErrorMessage(err));
      }
    })();
  }, [params, signInWithGoogle, navigate]);

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    if (challengeToken === null) return;

    setError(null);
    setVerifying(true);

    try {
      const result = await completeTwoFactor(challengeToken, code);
      navigate(destinationAfterLogin(null, result.user), { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  }

  // A Google account proves an address; it does not satisfy a second factor somebody turned on
  // here. Treating it as though it did would make every protected account reachable by anyone
  // holding the mailbox.
  if (challengeToken !== null) {
    return (
      <AuthLayout title="One more step" subtitle="Enter the code from your authenticator app.">
        <form onSubmit={onSubmitCode} className="space-y-4">
          {error !== null && <Alert variant="danger">{error}</Alert>}
          <div className="space-y-2">
            <Label htmlFor="code">Authentication code</Label>
            <Input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
          <Button type="submit" className="w-full" disabled={verifying || code.trim() === ''}>
            {verifying && <Loader2 className="mr-2 size-4 animate-spin" />}
            Verify
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (error !== null) {
    return (
      <AuthLayout title="Sign-in failed" subtitle="">
        <div className="space-y-4">
          <Alert variant="danger">{error}</Alert>
          <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Signing you in" subtitle="One moment.">
      <div className="flex justify-center py-6">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    </AuthLayout>
  );
}
